// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: link a personal WhatsApp by scanning a QR code, exactly like
// WhatsApp Web. Opt-in: nothing WhatsApp shows anywhere until this is linked.

import {KonvoConnectorCard} from '@app/features/konvo_connections/components/KonvoConnectorCard';
import styles from '@app/features/konvo_connections/components/KonvoConnectorCard.module.css';
import KonvoWhatsApp from '@app/features/konvo_whatsapp/state/KonvoWhatsApp';
import {WhatsAppLogo} from '@app/features/ui/components/icons/WhatsAppLogo';
import {QRCodeCanvas} from '@app/features/ui/components/QRCodeCanvas';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';

export const KonvoWhatsAppCard = observer(({solo = false}: {solo?: boolean}) => {
	const {t} = useLingui();
	const status = KonvoWhatsApp.status;
	const linked = KonvoWhatsApp.linked;
	const pairing = status?.state === 'pairing';
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		void KonvoWhatsApp.ensureStatus(10_000);
	}, []);

	if (status && !status.available) return null;

	const account = linked
		? status?.state === 'connected'
			? status.display_name || (status.phone ? `+${status.phone}` : t`Connected`)
			: t`Reconnecting…`
		: null;

	const run = async (work: () => Promise<void>) => {
		setBusy(true);
		try {
			await work();
		} finally {
			setBusy(false);
		}
	};

	return (
		<div style={{display: 'contents'}}>
			<KonvoConnectorCard
				logo={<WhatsAppLogo size={24} />}
				name="WhatsApp"
				description={t`Your personal WhatsApp chats inside Konvo, in their own panel. Never mixed with direct messages.`}
				features={[t`Chats and groups`, t`Photos, videos and files`, t`Also in the Rail`]}
				connected={linked}
				account={account}
				actionLabel={linked ? t`Unlink` : pairing ? t`Cancel` : t`Link WhatsApp`}
				actionVariant={linked || pairing ? 'secondary' : 'primary'}
				onAction={() => void run(() => (linked || pairing ? KonvoWhatsApp.unlink() : KonvoWhatsApp.link()))}
				busy={busy}
				solo={solo}
				data-flx="konvo-connections.whatsapp-card.konvo-connector-card"
			/>
			{pairing && (
				<div className={styles.card} style={{alignItems: 'center', textAlign: 'center', gap: 10}}>
					{status?.qr ? (
						<>
							<div style={{background: '#fff', borderRadius: 12, padding: 6, lineHeight: 0}}>
								<QRCodeCanvas data={status.qr} size={220} />
							</div>
							<div style={{fontSize: 13, color: 'var(--text-primary-muted)', maxWidth: 300}}>
								{t`On your phone open WhatsApp, go to Linked devices, tap Link a device and scan this code.`}
							</div>
						</>
					) : (
						<div style={{fontSize: 13, color: 'var(--text-primary-muted)'}}>{t`Preparing the code…`}</div>
					)}
				</div>
			)}
		</div>
	);
});
